// src/components/DetailSidebar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ShieldAlert, Share2, Download, Loader2, Save, Fingerprint, Zap, Maximize2, X, Copy, Eye, EyeOff, Sparkles,
  Calendar, Key, Info, Globe, Monitor, MapPin, Activity, HardDrive, Shield, Network, Terminal, Cpu, ShieldCheck, FileWarning, Hash, Search
} from "lucide-react";

import { SlackIcon } from 'lucide-react';
import { SiJira } from "react-icons/si";
import FrostedModal from "./modals/FrostedModal";
import { MODAL_CONFIG, MODAL_TYPE, resolveModalFromApi } from "../utils/errors";
import { apiFetch, ensureAuthOrRedirect, redirectToLogin, attachExpiryTimer, getToken } from "../utils/auth";
import { useNavigate } from "react-router-dom";

// Helper for email validation
const isEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export default function DetailSidebar({ open, row, initialParams, jwtToken, onClose, onUnlocked }) {
  // TABS is computed further down (after systemInfos) so it can show
  // "Downloads" only when at least one archive is downloadable. Default
  // active tab stays "Summary".
  const [tab, setTab] = useState("Summary");
  const scrollRef = useRef(null);
  // Reset the content scroll to the top whenever the tab changes, so switching
  // from a scrolled-down Summary to History/Downloads starts at the top.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [tab]);
  const [checkingAccess, setCheckingAccess] = useState(null); // Stores the username of the active check
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [data, setData] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [slackPushing, setSlackPushing] = useState(false);
  const [slackPushStatus, setSlackPushStatus] = useState(null);
  const [archiveDownloading, setArchiveDownloading] = useState(false);
  const navigate = useNavigate();

  const [modal, setModal] = useState({ open: false, type: null });
  // Holds the server-provided { title, message } for a security-restriction
  // (anti-automation strike) 403, so the modal can show the exact backend copy.
  const [restriction, setRestriction] = useState(null);
  const openModalFor = (type) => setModal({ open: true, type });
  const closeModal = () => {
    setModal({ open: false, type: null });
    setRestriction(null);
    setSaveStatus(null);   // clears inline banner
    setError("");          // clears error text in content area
    setTab("Summary");
  };
  const cfg = modal.type ? MODAL_CONFIG[modal.type] : null;

  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingStatus, setDownloadingStatus] = useState(null);

  useEffect(() => {
    if (!open) {
      setTab("Summary");
      setSaveStatus(null);
      setError("");
      setSaving(false);
      setDownloading(false);
    }
  }, [open]);

  const abortRef = useRef(null);
  const canCall = open && initialParams && jwtToken;

  const DETAIL_URL =
    "https://whiteintel.io/api/regular/app/v2/globalsearch/get_incident_detail_v4.php";
  const UNLOCK_URL =
    "https://whiteintel.io/api/regular/app/v2/incidents/get_unlock_item_handler.php";

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Initial fetch on open
  useEffect(() => {
    if (!canCall) return;

    setLoading(true);
    setError("");
    setData(null);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      try {
        const res = await fetch(DETAIL_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(initialParams),
          signal: controller.signal,
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        // 403 (anti-automation strike, automation block, tier-license gate,
        // or "Forbidden search.") and 429 (rate limit) need a modal, not a
        // login bounce or a raw "HTTP 4xx". The 429 can originate at the
        // Cloudflare edge (our configured rate-limit rule) BEFORE the request
        // reaches PHP, so its body may be a custom JSON object, a JSON string,
        // or plain text rather than our { error, message } envelope. Read the
        // body as text first and normalise all of those shapes.
        if (res.status === 403 || res.status === 429) {
          let raw = "";
          try { raw = ((await res.text()) || "").trim(); } catch {}
          let payload = {};
          let textMsg = "";
          if (raw) {
            try {
              const j = JSON.parse(raw);
              if (typeof j === "string") textMsg = j.trim();
              else if (j && typeof j === "object") payload = j;
            } catch {
              // Not JSON (e.g. a Cloudflare plain-text body). Use it as the
              // message unless it's an HTML error page.
              if (!raw.startsWith("<")) textMsg = raw;
            }
          }
          const srvError = typeof payload?.error === "string" ? payload.error.trim() : "";
          const srvMessage =
            (typeof payload?.message === "string" ? payload.message.trim() : "") || textMsg;

          if (res.status === 429) {
            // Rate limit — most often our Cloudflare edge rule. Surface its
            // exact message when we have one, then point to the API as the
            // supported path for programmatic access.
            setRestriction({
              title: "Request Rate Exceeded",
              message: (
                <>
                  <p>
                    {srvMessage ||
                      "Requests from your session are arriving faster than the web interface permits. Please pause before continuing."}
                  </p>
                  <p className="mt-3">
                    Automated access is limited to prevent data scraping and the
                    circumvention of API usage limits. For programmatic, bulk, or
                    high-frequency access, use the{" "}
                    <a
                      href="https://docs.whiteintel.io"
                      target="_blank"
                      rel="noreferrer"
                      className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline font-medium"
                    >
                      Whiteintel API
                    </a>
                    , the supported channel for automation and integration.
                  </p>
                </>
              ),
            });
          } else if (srvError && srvMessage) {
            // Anti-automation responses carry BOTH fields. This is an
            // anti-scraping usage control on the UI — not a security incident —
            // so the copy frames it as a usage policy, states when access is
            // restored, warns about permanent suspension, and directs legitimate
            // automation to the API.
            //
            // The daily-cap 403 ships a precise `restricted_until` (ISO-8601) /
            // `retry_after_seconds`; use those for an exact lift time. The
            // shorter strike 403 ships neither, so fall back to "within 6 hours".
            const resetIso =
              typeof payload?.restricted_until === "string" ? payload.restricted_until : "";
            const resetDate = resetIso ? new Date(resetIso) : null;
            const resetLabel =
              resetDate && !Number.isNaN(resetDate.getTime())
                ? resetDate.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                : null;
            setRestriction({
              title: "Automated Access Restricted",
              message: (
                <>
                  <p>
                    Access to this resource has been temporarily suspended.
                    Activity from your session matched a pattern associated with
                    automated requests. Automated access through the web interface
                    is restricted to prevent large-scale data scraping and the
                    circumvention of API usage limits.
                  </p>
                  <p className="mt-3">
                    {resetLabel ? (
                      <>
                        Access will be restored automatically on{" "}
                        <span className="text-white font-medium">{resetLabel}</span>.
                      </>
                    ) : (
                      <>
                        This restriction will be lifted automatically within{" "}
                        <span className="text-white font-medium">6 hours</span>.
                      </>
                    )}
                  </p>
                  <p className="mt-3">
                    Continued automated activity may result in a{" "}
                    <span className="text-white font-medium">
                      permanent suspension
                    </span>{" "}
                    of your account.
                  </p>
                  <p className="mt-3">
                    If your workflow requires programmatic or high-volume access
                    to this data, use the{" "}
                    <a
                      href="https://docs.whiteintel.io"
                      target="_blank"
                      rel="noreferrer"
                      className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline font-medium"
                    >
                      Whiteintel API
                    </a>
                    , the supported and intended channel for automation.
                  </p>
                </>
              ),
            });
          } else {
            // Informational restriction (tier-license gate, restricted search)
            // — carries a single, specific field worth surfacing verbatim.
            setRestriction({
              title: "Access Restricted",
              message:
                srvMessage ||
                srvError ||
                "Access to this resource is currently restricted.",
            });
          }
          openModalFor(MODAL_TYPE.SECURITY_RESTRICTION);
          return; // stop here; finally still clears the loading state
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setData(json);

      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Failed to load details");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    run();

    return () => controller.abort();
  }, [open, initialParams, jwtToken, canCall]);

  const isLocked = data && data.is_unlocked === false;
  const systemInfos = data?.data?.systeminfo || [];

  const metaFirstSeen = systemInfos.length > 0 ? systemInfos[systemInfos.length - 1].log_date : "—";
  const metaLastSeen  = systemInfos.length > 0 ? systemInfos[0].log_date : "—";

const getArchiveState = (info) => {
  const status = info?.archive_status;
  if (status === "uploaded") {
    return { tone: "available", label: "Available", downloadable: true };
  }
  if (status === "deduplicated") {
    return { tone: "dedup", label: "Deduplicated", downloadable: true };
  }
  if (status === "pending") {
    return { tone: "pending", label: "Processing", downloadable: false };
  }
  if (status === "failed") {
    return { tone: "failed", label: "Upload failed", downloadable: false };
  }
  return { tone: "none", label: "No archive", downloadable: false };
};

  // Downloadable subset — drives both the new "Downloads" tab visibility
  // and the count badge on it. Order mirrors systemInfos (newest first).
  const downloadableInfos = systemInfos.filter((info) => getArchiveState(info).downloadable);
  const TABS = downloadableInfos.length > 0
    ? ["Summary", "History", "Downloads"]
    : ["Summary", "History"];

  // If the user is sitting on the Downloads tab and the archive availability
  // disappears (re-fetch, item swap), drop them back to Summary.
  useEffect(() => {
    if (tab === "Downloads" && downloadableInfos.length === 0) {
      setTab("Summary");
    }
  }, [tab, downloadableInfos.length]);

  // Pretty-print archive byte counts (1.2 GB, 768 MB, etc.) — used in the
  // Downloads tab card. Falls back to "—" when the field is missing.
  const formatBytes = (n) => {
    const b = Number(n);
    if (!Number.isFinite(b) || b <= 0) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0, v = b;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
  };

  const historyHasMissingMetadata = useMemo(() => {
  if (!systemInfos || systemInfos.length <= 1) return false;

  const latest = systemInfos[0];
  const historical = systemInfos.slice(1);

  // The fields you identified as important
  const criticalFields = ["hostname", "ip", "country", "os", "anti_viruses", "malware_path"];

  return criticalFields.some((field) => {
    const isMissingInLatest = !latest[field] || String(latest[field]).trim() === "" || latest[field] === "N/A";
    const existsInHistory = historical.some(
      (h) => h[field] && String(h[field]).trim() !== "" && h[field] !== "N/A"
    );
    return isMissingInLatest && existsInHistory;
  });
}, [systemInfos]);

  const handlePushToSlack = async () => {
    setSlackPushing(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/slack/send_slack.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: Number(initialParams.id),
            query: String(initialParams.query || ""),
            type: String(initialParams.type || ""),
            detail_type: String(initialParams.detail_type || ""),
          }),
        }
      );

      let result = {};
      try { result = await res.json(); } catch (e) { result = {}; }

      if (!res.ok || result.status !== "success") {
        setPushStatus({
          type: "error",
          message: result?.message || result?.error || `Failed to push to Slack.`,
        });
        return;
      }

      setSlackPushStatus({
        type: "success",
        message: result.message || "Pushed to Slack successfully",
      });
    } catch (err) {
      setPushStatus({ type: "error", message: "Unexpected error while pushing to Slack." });
    } finally {
      setSlackPushing(false);
    }
  };

  const handlePushToJira = async () => {
    setPushing(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/jira/create_jira_ticket.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: Number(initialParams.id),
            query: String(initialParams.query || ""),
            type: String(initialParams.type || ""),
            detail_type: String(initialParams.detail_type || ""),
          }),
        }
      );

      let result = {};
      try { result = await res.json(); } catch (e) { result = {}; }

      if (!res.ok || result.status !== "success") {
        setPushStatus({
          type: "error",
          message: result?.message || result?.error || `Failed to push to Jira.`,
        });
        return;
      }

      setPushStatus({
        type: "success",
        message: (
          <>
            {result.message || "Pushed to Jira successfully"}{" "}
            {result.jira_ticket && result.ticket_url && (
              <>
                (<a href={result.ticket_url} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">{result.jira_ticket}</a>)
              </>
            )}
          </>
        ),
      });

    } catch (err) {
      setPushStatus({ type: "error", message: "Unexpected error while pushing to Jira." });
    } finally {
      setPushing(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveStatus(null);

    try {
      const response = await fetch(
        "https://whiteintel.io/api/regular/app/v2/incidents/save_incident.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: Number(initialParams.id),
            query: String(initialParams.query || ""),
            detail: String(initialParams.type || ""),
            breach_type: String(initialParams.detail_type),
          }),
        }
      );

      if ([401, 419, 440].includes(response.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${response.status}`);
      }

      const result = await response.json();

      if (response.status === 403 && result.message?.includes("Forbidden search")) {
        openModalFor("FORBIDDEN_SEARCH");
        return;
      }

      if (result.is_saved) {
        setSaveStatus({ type: "success", message: "Incident saved successfully." });
      } else {
        setSaveStatus({ type: "error", message: result.error || "Incident was not saved." });
      }
    } catch (err) {
      setSaveStatus({ type: "error", message: "Something went wrong while saving the incident." });
    } finally {
      setSaving(false);
    }
  };


const handleNeuralAnalysis = async (username) => {
  if (checkingAccess) return; // Prevent concurrent clicks
  
  setCheckingAccess(username);
  try {
    const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/check_access.php", {
      headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}` 
      }
    });
    const json = await res.json();

    if (json.success && json.subscription === 'tifirm') {
      window.open(`/investigations?query=${encodeURIComponent(username)}`, '_blank');
    } else {
      setShowUpgradeModal(true);
    }
  } catch (err) {
    console.error("Access check failed", err);
    setShowUpgradeModal(true);
  } finally {
    setCheckingAccess(null); // Reset loading state
  }
};


const [archiveDownloadingId, setArchiveDownloadingId] = useState(null);
const [pendingDownloadId, setPendingDownloadId] = useState(null);
// Per-credential "view more" state for the source_ids chip strip. Keyed by
// the credential's index in the response; resets implicitly when a new
// detail loads because we always render against fresh data.
const [expandedCredSources, setExpandedCredSources] = useState(() => new Set());
const toggleCredSources = (idx) => {
  setExpandedCredSources((prev) => {
    const next = new Set(prev);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    return next;
  });
};

// Per-credential cookie fetch state. Keyed by the credential's original
// index in the response. Shape: { loading, visible, data, error }.
const [cookieByIdx, setCookieByIdx] = useState({});
// Per-credential search query for the cookie list. Keyed by origIdx.
const [cookieSearchByIdx, setCookieSearchByIdx] = useState({});
// Tracks the credential whose "Copy all" was just clicked, so the
// button can flip to "Copied" for ~1.5s before reverting.
const [copiedAllForIdx, setCopiedAllForIdx] = useState(null);
const handleCopyAllCookies = (origIdx, cookies) => {
  try {
    navigator.clipboard.writeText(JSON.stringify(cookies, null, 2));
  } catch {}
  setCopiedAllForIdx(origIdx);
  setTimeout(() => {
    setCopiedAllForIdx((c) => (c === origIdx ? null : c));
  }, 1500);
};
const handleViewCookie = async (origIdx, cred) => {
  const current = cookieByIdx[origIdx];
  // Already loaded — toggle visibility without re-fetching.
  if (current && current.data != null) {
    setCookieByIdx((prev) => ({
      ...prev,
      [origIdx]: { ...prev[origIdx], visible: !prev[origIdx].visible },
    }));
    return;
  }
  if (current?.loading) return;
  setCookieByIdx((prev) => ({
    ...prev,
    [origIdx]: { loading: true, visible: true, data: null, error: null },
  }));
  try {
    const res = await apiFetch(
      "https://whiteintel.io/api/regular/app/v2/globalsearch/get_cookies.php",
      {
        method: "POST",
        body: JSON.stringify({
          url: cred.url || "",
          source_ids: Array.isArray(cred.source_ids) ? cred.source_ids : [],
          username: cred.username || "",
        }),
      }
    );
    const payload = await res.json().catch(() => ({}));
    if (!res.ok || payload?.error || payload?.status === "error") {
      throw new Error(payload?.error || payload?.message || `Could not fetch cookies (${res.status}).`);
    }
    setCookieByIdx((prev) => ({
      ...prev,
      [origIdx]: {
        loading: false,
        visible: true,
        data: {
          domain: payload.domain || "",
          total: payload.total ?? (Array.isArray(payload.cookies) ? payload.cookies.length : 0),
          cookies: Array.isArray(payload.cookies) ? payload.cookies : [],
        },
        error: null,
      },
    }));
  } catch (e) {
    setCookieByIdx((prev) => ({
      ...prev,
      [origIdx]: {
        loading: false,
        visible: true,
        data: null,
        error: e?.message || "Failed to fetch cookie.",
      },
    }));
  }
};

useEffect(() => {
  if (!pendingDownloadId) return;
  const onKey = (e) => {
    if (e.key === "Escape") setPendingDownloadId(null);
  };
  // Close when clicking anywhere outside the popover (excluding the trigger).
  const onDocMouseDown = (e) => {
    if (e.target.closest && e.target.closest("[data-archive-warning]")) return;
    setPendingDownloadId(null);
  };
  document.addEventListener("keydown", onKey);
  document.addEventListener("mousedown", onDocMouseDown);
  return () => {
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("mousedown", onDocMouseDown);
  };
}, [pendingDownloadId]);

const handleDownloadArchive = async (systeminfoId) => {
  if (!systeminfoId || archiveDownloadingId) return;

  setArchiveDownloadingId(systeminfoId);
  setSaveStatus(null);

  try {
    const res = await fetch(
      `https://whiteintel.io/api/regular/app/v2/globalsearch/download_archive.php?systeminfo_id=${encodeURIComponent(systeminfoId)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      throw new Error(`AUTH_REDIRECT_${res.status}`);
    }

    let payload = {};
    try { payload = await res.json(); } catch { payload = {}; }

    if (res.status === 403) {
      setSaveStatus({
        type: "error",
        message: payload?.error || "Full log archive downloads require the Threat Intelligence subscription.",
        cta: { label: "Upgrade", href: "/upgrades" },
      });
      return;
    }

    if (res.status === 429) {
      const retry = payload?.retry_after_seconds;
      setSaveStatus({
        type: "error",
        message: retry
          ? `Rate limit hit. Try again in ${Math.ceil(retry / 60)} minute(s).`
          : (payload?.error || "Too many downloads. Please slow down."),
      });
      return;
    }

    if (res.status === 404) {
      setSaveStatus({ type: "error", message: "No archive available for this log." });
      return;
    }

    if (res.status === 409) {
      setSaveStatus({
        type: "error",
        message: "Archive is still being prepared. Try again in a few minutes.",
      });
      return;
    }

    if (!res.ok || !payload?.success || !payload?.url) {
      setSaveStatus({
        type: "error",
        message: payload?.error || "Could not generate download link.",
      });
      return;
    }

    const a = document.createElement("a");
    a.href = payload.url;
    a.download = payload.filename || `log_${systeminfoId}.tar.zst`;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();

    const sizeMb = payload.size_bytes
      ? ` (${(payload.size_bytes / 1024 / 1024).toFixed(1)} MB)`
      : "";
    setSaveStatus({
      type: "success",
      message: `Archive download started.`,
    });
  } catch (err) {
    if (!String(err?.message || "").startsWith("AUTH_REDIRECT_")) {
      setSaveStatus({
        type: "error",
        message: "Unexpected error while preparing archive download.",
      });
    }
  } finally {
    setArchiveDownloadingId(null);
  }
};
  const downloadBreachReport = async (breachId, query, detailType) => {
    if (downloading) return;
    setDownloading(true);
    setSaveStatus(null);

    try {
      const response = await fetch(
        "https://whiteintel.io/api/regular/app/v2/globalsearch/get_download_as_txt_handler.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: Number(initialParams.id),
            query: String(initialParams.query || ""),
            type: String(initialParams.type || ""),
            detail_type: String(initialParams.detail_type),
          }),
        }
      );

      if ([401, 419, 440].includes(response.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${response.status}`);
      }

      if (!response.ok) {
        let payload;
        try { payload = await response.json(); } catch { payload = { message: await response.text() }; }
        if (response.status === 403 && /forbidden search/i.test(payload?.message || "")) {
          setSaveStatus({ type: "error", message: "Download unavailable under your current plan." });
          return;
        }
        setSaveStatus({ type: "error", message: fmtApiError(payload, "Download failed.") });
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeQuery = String(query || "").replace(/[^a-zA-Z0-9.-]/g, "_");
      a.href = url;
      a.download = `${breachId}_${safeQuery}_${detailType}_breach_details.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSaveStatus({ type: "success", message: "Report downloaded." });
    } catch (error) {
      setSaveStatus({ type: "error", message: `Download failed — ${error?.message || "Please try again."}` });
    } finally {
      setDownloading(false);
    }
  };

  const handleUnlock = async () => {
    if (!initialParams?.id) return;
    setUnlocking(true);
    setError("");
    const buildBody = () =>
      JSON.stringify({
        id: Number(initialParams.id),
        query: String(initialParams.query || ""),
        detail: String(initialParams.type || ""),
        ...(initialParams.detail_type === "emailAddress" && {
          search_type: String(initialParams.detail_type),
        }),
      });

    try {
      const res = await fetch(UNLOCK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: buildBody(),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const json = await res.json();
      const msg = String(json?.message || "").toLowerCase();

      if (json?.status === "error" || json?.success === false) {
        if (json?.is_unlocked === false && /insufficient.*unlock/i.test(msg)) {
          openModalFor(MODAL_TYPE.INSUFFICIENT_UNLOCK);
        } else {
          openModalFor(MODAL_TYPE.GENERIC_ERROR);
        }
        return;
      }

      const isSuccess = json?.success === true || String(json?.status || "").toLowerCase() === "success";

      if (isSuccess && json?.is_unlocked === true) {
        setError("");
        setData((prev) => (prev ? { ...prev, is_unlocked: true } : prev));
        setLoading(true);
        const refetch = await fetch(DETAIL_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(initialParams),
        });

        if (!refetch.ok) throw new Error(`Refetch HTTP ${refetch.status}`);
        const fresh = await refetch.json();
        setData(fresh);
        try { onUnlocked?.(); } catch { }
      } else {
        setError(json?.message || "Unlock failed");
        return;
      }
    } catch (e) {
      setError(e.message || "Unlock failed");
    } finally {
      setUnlocking(false);
      setLoading(false);
    }
  };

 const compromisedFields = useMemo(() => {
  if (initialParams?.type !== "database" || !data?.data?.data_fields) return [];
  return data.data.data_fields.split(",").map(f => f.trim());
}, [data, initialParams]);

  const meta = useMemo(() => {
    const m = data?.meta || data || {};
    return {
      logId: String(m.log_id || row?.log_id || initialParams?.id || "—"),
      credentials: Array.isArray(m.credentials) ? m.credentials : (Array.isArray(data?.credentials) ? data.credentials : []),
    };
  }, [data, row, initialParams]);

  const Loader = () => (
    <div className="p-5 space-y-5">
      <div className="h-5 w-36 bg-neutral-800/60 animate-pulse rounded" />
      <div className="space-y-3">
        <div className="h-4 w-64 bg-neutral-800/60 animate-pulse rounded" />
        <div className="h-4 w-48 bg-neutral-800/60 animate-pulse rounded" />
      </div>
      <div className="space-y-2">
        <div className="h-4 w-full bg-neutral-800/60 animate-pulse rounded" />
        <div className="h-4 w-5/6 bg-neutral-800/60 animate-pulse rounded" />
      </div>
    </div>
  );

  return (
    <div className={`fixed inset-0 z-[80] ${open ? "" : "pointer-events-none"}`} role="dialog" aria-modal="true">
      <div className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
<aside className={`absolute right-0 top-0 h-full w-full max-w-5xl border-l border-sky-900/40 bg-[#06101e] shadow-[0_0_50px_rgba(0,0,0,0.5)] transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>        <div className="flex items-start justify-between px-5 py-4 border-b border-sky-900/30">
          <div>
            <div className="flex items-center gap-2 text-[13px] text-neutral-400"><ShieldAlert className="h-4 w-4" /><span>Incident Detail</span></div>
            <h2 className="mt-1 text-[16px] font-semibold tracking-tight">Incident Detail · {meta.logId}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button title="Push to Jira" onClick={handlePushToJira} disabled={pushing} className="flex h-6 w-6 items-center justify-center text-gray-300 hover:text-gray-400 disabled:opacity-50">
              {pushing ? <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg> : <SiJira className="h-4 w-4" />}
            </button>
            <button title="Push to Slack" onClick={handlePushToSlack} disabled={slackPushing} className="flex h-6 w-6 items-center justify-center text-gray-300 hover:text-gray-400 disabled:opacity-50">
              {slackPushing ? <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg> : <SlackIcon className="h-4 w-4" />}
            </button>
            <IconBtn title="Save" onClick={handleSave}>{saving ? <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25"/><path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" className="opacity-75"/></svg> : <Save className="h-4 w-4" />}</IconBtn>
           

            <IconBtn title="Download" onClick={() => downloadBreachReport(Number(initialParams.id), String(initialParams.query || ""), String(initialParams.type || ""))} disabled={downloading}>{downloading ? <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/><path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" className="opacity-75"/></svg> : <Download className="h-4 w-4" />}</IconBtn>
            <IconBtn title="Close" onClick={onClose}><X className="h-4 w-4" /></IconBtn>
          </div>
        </div>

        {(saveStatus || pushStatus || slackPushStatus) && (
          <div className={`mx-5 mt-3 flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-[13px] ${(saveStatus?.type === "success" || pushStatus?.type === "success" || slackPushStatus?.type === "success") ? "border-green-500/30 bg-green-500/10 text-green-200" : "border-red-500/30 bg-red-500/10 text-red-200"}`}>
            <span className="min-w-0">{saveStatus?.message || pushStatus?.message || slackPushStatus?.message}</span>
            <div className="flex items-center gap-2 shrink-0">
              {saveStatus?.cta && (
                <a
                  href={saveStatus.cta.href}
                  className="rounded-md border border-sky-500/50 bg-sky-500/15 px-2.5 py-1 text-xs font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/70 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                >
                  {saveStatus.cta.label}
                </a>
              )}
              <button onClick={() => { setSaveStatus(null); setPushStatus(null); setSlackPushStatus(null); }} className="text-xs text-neutral-400 hover:text-white">Dismiss</button>
            </div>
          </div>
        )}

{/* Tab nav — matches the WatchlistEvents / BrandProtection / PaymentFraud
    sub-nav strip pattern: gap-5, thin dividers, 3px bottom border on the
    active tab that overlaps the container's hairline border. */}
{initialParams?.type === "stealer" && (
  <div className="border-b border-white/10">
    <nav className="px-5 flex items-center gap-5 flex-wrap" role="tablist" aria-label="Incident detail sections">
      {TABS.map((t, i) => {
        const active = tab === t;
        return (
          <React.Fragment key={t}>
            {i > 0 && <span aria-hidden className="h-3.5 w-px bg-neutral-700/60 -mb-px" />}
            <button
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t)}
              className={`py-2 text-[13px] font-medium transition whitespace-nowrap -mb-px border-b-[3px] inline-flex items-center gap-1.5 ${
                active
                  ? "text-white border-neutral-200"
                  : "text-neutral-400 hover:text-white border-transparent"
              }`}
            >
              {t}
              {t === "History" && systemInfos.length > 1 && (
                <span className="inline-flex items-center justify-center rounded-full bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-bold text-sky-300 border border-sky-500/30">
                  {systemInfos.length}
                </span>
              )}
              {t === "Downloads" && downloadableInfos.length > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-bold text-sky-300 border border-sky-500/30">
                  {downloadableInfos.length}
                </span>
              )}
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  </div>
)}

        {showUpgradeModal && (
  <div 
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-[6px] animate-in fade-in duration-300"
    onClick={() => setShowUpgradeModal(false)}
  >
    <div 
      className="relative w-full max-w-2xl rounded-md border border-white/10 bg-[#000414] shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden animate-in zoom-in-95 duration-300"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => setShowUpgradeModal(false)}
        className="absolute top-4 right-4 z-50 rounded-md p-1.5 bg-black/20 text-neutral-500 hover:text-white hover:bg-white/10 transition-colors border border-white/5"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="px-8 py-6 border-b border-sky-900/30 bg-[#0a1224]/60 flex items-center gap-4">
         <div className="w-11 h-11 rounded-md bg-[#000414] border border-sky-500/30 flex items-center justify-center shrink-0">
           <Sparkles className="w-5 h-5 text-sky-400" />
         </div>
         <div className="min-w-0">
            <div className="text-[10.5px] font-semibold text-sky-300/70 uppercase tracking-[0.18em] mb-1">
              Whiteintel AI Assistant
            </div>
            <h3 className="text-[18px] font-semibold text-white tracking-tight leading-snug">Forensic Identity Attribution</h3>
         </div>
      </div>

      <div className="px-8 py-6">
        <div className="mb-6">
          <p className="text-[13px] text-neutral-400 leading-relaxed">
            Leverage autonomous heuristics to de-anonymize targets and map lateral exposure. 
            <span className="block mt-1 text-sky-400 font-semibold italic text-xs">
              Threat Intel license is required for Whiteintel AI Assistant.
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {[
            { icon: <Fingerprint className="w-4 h-4 text-sky-400" />, title: "Identity Correlation", desc: "Resolve real names, phone numbers & Govt IDs." },
            { icon: <Zap className="w-4 h-4 text-amber-400" />, title: "Neural Scoring", desc: "Prioritize critical assets via AI prioritization." },
            { icon: <ShieldAlert className="w-4 h-4 text-emerald-400" />, title: "Footprinting", desc: "Trace historical identity exposure across logs." }
          ].map((feat, i) => (
            <div key={i} className="flex flex-col gap-2 p-3.5 rounded-md bg-white/[0.02] border border-sky-900/30 hover:bg-white/[0.04] transition-colors">
              <div className="flex items-center gap-2">
                {feat.icon}
                <p className="text-[11px] font-semibold text-neutral-200 tracking-tight">{feat.title}</p>
              </div>
              <p className="text-[11px] text-neutral-500 leading-snug">{feat.desc}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row items-center gap-4">
          <button
            onClick={() => window.location.href = '/settings/billing'}
            className="w-full md:flex-1 py-2.5 rounded-md border border-sky-500/40 bg-sky-500/15 hover:bg-sky-500/25 text-sky-100 font-semibold text-sm transition-colors"
          >
            Upgrade to Threat Intel
          </button>
          <button
            onClick={() => setShowUpgradeModal(false)}
            className="w-full md:w-auto px-6 py-2 text-neutral-500 hover:text-neutral-300 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
)}

<div ref={scrollRef} className="overflow-y-auto p-5 pb-24 h-[calc(100%-100px)] relative">
  {loading ? (
    <Loader />
  ) : error ? (
    <div className="p-5 text-red-400 text-[13px]">Error: {error}</div>
  ) : (
    <>
      <div className={isLocked ? "blur-sm select-none" : ""}>
        {tab === "Summary" && (
          <div className="space-y-8">
            {/* --- DATABASE LEAK VIEW (HIBP STYLE) --- */}
            {initialParams?.type === "database" ? (
        <>
    <Section title="Breach Overview">
      <div className="rounded-md border border-indigo-500/20 bg-indigo-500/5 p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-indigo-400/30 bg-[#000414] shadow-lg">
            <Shield className="h-6 w-6 text-indigo-400" />
          </div>
          <div className="space-y-1">
            <h3 className="text-[14px] font-semibold text-white">
              {data?.data?.database_name || "Unknown Breach"}
            </h3>
            <div className="flex items-center gap-2 text-xs text-indigo-300/70">
              <Calendar className="h-3.5 w-3.5" />
              <span>Breach Date: {data?.data?.breach_date || "—"}</span>
            </div>
          </div>
        </div>
        <p className="mt-4 text-[13px] leading-relaxed text-neutral-300">
          {data?.data?.breach_description ||
            "This identity was found in a publicly circulated database leak. Database leaks typically contain structured information harvested from compromised web services."}
        </p>
      </div>
    </Section>

    <Section title="Compromised Data">
      <div className="flex flex-wrap gap-2">
        {data?.data?.data_fields ? (
          data.data.data_fields.split(",").map((field, i) => (
            <span key={i} className="flex items-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-800/40 px-3 py-1.5 text-xs font-medium text-neutral-200">
              <div className="h-1 w-1 rounded-full bg-indigo-400" />
              {field.trim().replace(/_/g, " ")}
            </span>
          ))
        ) : (
          <span className="text-[13px] text-neutral-500 italic">No field data recorded.</span>
        )}
      </div>
    </Section>

    <Section title={`Exposed Accounts (${data?.data?.total || 0})`}>
  <div className="max-h-[400px] overflow-y-auto pr-1 space-y-3 scrollbar-thin scrollbar-thumb-neutral-700 scrollbar-track-transparent">

        {data?.data?.accounts?.length ? (
          data.data.accounts.map((account, idx) => (
            <div key={idx} className="p-3 rounded-md bg-neutral-900/40 border border-neutral-800 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[13px] text-neutral-400">Email:</div>
                <div className="flex items-center gap-2">
                  <div className="text-neutral-100 font-medium truncate">{account.email || "—"}</div>
                  {account.email && <CopyBtn text={account.email} />}
                </div>
              </div>

              {account.password && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-2">
                  <div className="text-[13px] text-neutral-400">Password:</div>
                  <div className="flex items-center gap-2">
                    <PasswordField password={account.password} />
                    <CopyBtn text={account.password} />
                  </div>
                </div>
              )}

              {account.hashed_password && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-2">
                  <div className="text-[13px] text-neutral-400">Hash:</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-indigo-300/80 bg-indigo-500/10 px-2 py-1 rounded truncate max-w-[200px]" title={account.hashed_password}>
                      {account.hashed_password}
                    </span>
                    <CopyBtn text={account.hashed_password} />
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-[13px] text-neutral-500 italic">No account records available.</div>
        )}
      </div>
    </Section>

    <Section title="Remediations">
      <div className="space-y-3">
        <div className="flex gap-3 items-start">
          <div className="mt-1 h-5 w-5 rounded bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-[10px] font-bold">1</div>
          <p className="text-[13px] text-neutral-300">Change your password immediately for this service and any other accounts using the same credentials.</p>
        </div>
        <div className="flex gap-3 items-start">
          <div className="mt-1 h-5 w-5 rounded bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-[10px] font-bold">2</div>
          <p className="text-[13px] text-neutral-300">Enable Multi-Factor Authentication (MFA/2FA) on all sensitive professional and personal accounts.</p>
        </div>
        <div className="flex gap-3 items-start">
          <div className="mt-1 h-5 w-5 rounded bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-[10px] font-bold">3</div>
          <p className="text-[13px] text-neutral-300">Be alert for targeted phishing attempts using the personal details leaked in this breach.</p>
        </div>
      </div>
    </Section>
  </>
            ) : (
              /* --- STEALER / COMBOLIST VIEW --- */
              <>
                <Section title="What happened">
                  <p className="text-[13px] text-neutral-300 leading-relaxed">
                    {initialParams?.type === "combolist"
                      ? `Through our dark web monitoring operations, we identified that the user’s credentials were found in leaked credential lists. The most recent occurrence was observed on ${row.last_seen || data?.data?.credentials?.[0]?.log_date || "—"}.`
                      : `Through our dark web monitoring operations, we identified that the following device and credentials have been compromised by information stealers. The most recent occurrence was observed on ${data?.data?.systeminfo?.[0]?.log_date || "—"}.`}
                    {data?.data?.systeminfo?.length > 1 && (
                      <span className="block mt-2">{`Devices of this user has been detected as compromised ${data.data.systeminfo.length} times. ${data.data.systeminfo.length - 1} previous records available in History.`}</span>
                    )}
                  </p>
                </Section>

                <Section title="Meta Data">
                  {historyHasMissingMetadata && (
                    <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-500/20 bg-amber-500/5 p-3 text-[12px] leading-relaxed text-amber-200/80">
                      <p>
                        Metadata incomplete. 
                        <button onClick={() => setTab("History")} className="ml-1 font-bold text-amber-500 underline hover:text-amber-400">
                          Check History
                        </button>
                        {" "}for additional metadata records.
                      </p>
                    </div>
                  )}

                  {data?.data?.systeminfo?.length ? (
                    <div className="overflow-hidden rounded-md border border-neutral-800 bg-[#0f172a]/40 shadow-sm">
                      <div className="flex flex-col border-b border-neutral-800 md:flex-row">
                        <div className="flex-1 border-b border-neutral-800 p-4 md:border-b-0 md:border-r">
                          <EnterpriseField icon={<Calendar className="h-3.5 w-3.5" />} label="First Seen" value={metaFirstSeen} highlight />
                        </div>
                        <div className="flex-1 p-4">
                          <EnterpriseField icon={<Activity className="h-3.5 w-3.5" />} label="Last Seen" value={metaLastSeen} highlight />
                        </div>
                      </div>
                      <div className="bg-white/[0.01] p-5">
                        <Grid>
                          {Object.entries(data.data.systeminfo[0]).map(([key, value]) => {
                            if (key === "log_date") return null;
                            const lowerKey = key.toLowerCase();
                            let icon = <Hash className="h-3.5 w-3.5" />;
                            if (lowerKey.includes("ip")) icon = <Network className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("country")) icon = <Globe className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("host") || lowerKey.includes("computer")) icon = <Terminal className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("os") || lowerKey.includes("windows")) icon = <Cpu className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("virus") || lowerKey.includes("defender")) icon = <ShieldCheck className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("path") || lowerKey.includes("file")) icon = <FileWarning className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("id") || lowerKey.includes("guid")) icon = <Fingerprint className="h-3.5 w-3.5" />;

                            return <EnterpriseField key={key} icon={icon} label={key.replace(/_/g, " ")} value={value} />;
                          })}
                        </Grid>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-20 items-center justify-center rounded-md border border-neutral-800 border-dashed text-[13px] text-neutral-500">
                      No system metadata available.
                    </div>
                  )}
                </Section>

                <Section title="Compromised Credentials">
                  <div className="space-y-4">
                    {data?.data?.credentials?.length ? (
                      (() => {
                        // Pull the freshest systeminfo id, then surface any
                        // credentials whose source_ids reference it. Those
                        // credentials get grouped under a "From latest log"
                        // section header — one header for the whole group,
                        // not a per-card badge (which read as confusing
                        // when multiple credentials matched the same log).
                        const latestSysId = systemInfos?.[0]?.systeminfo_id;
                        const sortedCreds = data.data.credentials
                          .map((c, i) => ({
                            cred: c,
                            origIdx: i,
                            isLatest:
                              latestSysId != null &&
                              Array.isArray(c.source_ids) &&
                              c.source_ids.includes(latestSysId),
                          }))
                          .sort((a, b) => {
                            if (a.isLatest !== b.isLatest) return a.isLatest ? -1 : 1;
                            return a.origIdx - b.origIdx;
                          });
                        const latestCount = sortedCreds.filter((c) => c.isLatest).length;
                        const olderCount = sortedCreds.length - latestCount;

                        return sortedCreds.map(({ cred, origIdx, isLatest }, sortedIdx) => {
                          const sourceIds = Array.isArray(cred.source_ids) ? cred.source_ids : [];
                          const isExpanded = expandedCredSources.has(origIdx);
                          const visibleIds = isExpanded ? sourceIds : sourceIds.slice(0, 3);
                          const hiddenCount = sourceIds.length - visibleIds.length;
                          const cookieState = cookieByIdx[origIdx];
                          // Group-separator logic: a single header above the
                          // first latest-matching cred, and another above the
                          // first older cred (only if both groups exist).
                          const showLatestHeader = isLatest && sortedIdx === 0 && latestCount > 0;
                          const showOlderHeader =
                            !isLatest && sortedIdx === latestCount && latestCount > 0 && olderCount > 0;
                          return (
                        <React.Fragment key={origIdx}>
                          {showLatestHeader && (
                            <div className="flex items-center gap-2 pt-1">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-400" />
                              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-sky-300/90">
                                From latest log
                              </span>
                              <span className="text-[10px] text-neutral-500">
                                · {latestCount} {latestCount === 1 ? "record" : "records"}
                              </span>
                              <span className="flex-1 h-px bg-neutral-800 ml-1" />
                            </div>
                          )}
                          {showOlderHeader && (
                            <div className="flex items-center gap-2 pt-2">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-500" />
                              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-400">
                                Older logs
                              </span>
                              <span className="text-[10px] text-neutral-500">
                                · {olderCount} {olderCount === 1 ? "record" : "records"}
                              </span>
                              <span className="flex-1 h-px bg-neutral-800 ml-1" />
                            </div>
                          )}
                        <div
                          className="p-3 rounded-md bg-neutral-900/40 border border-neutral-800"
                        >
                          {/* Source IDs — sit above the Application row so the
                              provenance of the leaked credential is the first
                              thing the analyst sees. First 3 shown by default,
                              "View more" toggles the rest. */}
                          {sourceIds.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <div className="text-[13px] text-neutral-400 shrink-0">Source IDs:</div>
                              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                {visibleIds.map((sid) => (
                                  <span
                                    key={sid}
                                    className="inline-flex items-center font-mono tabular-nums text-[11px] rounded px-1.5 py-0.5 ring-1 ring-inset bg-neutral-500/10 text-neutral-300 ring-neutral-500/30"
                                  >
                                    {sid}
                                  </span>
                                ))}
                                {sourceIds.length > 3 && (
                                  <button
                                    type="button"
                                    onClick={() => toggleCredSources(origIdx)}
                                    className="text-[11px] font-medium text-neutral-400 hover:text-neutral-200 underline-offset-2 hover:underline"
                                  >
                                    {isExpanded ? "View less" : `View more (+${hiddenCount})`}
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          <div className="mt-2 flex items-center gap-2 min-w-0">
                            <div className="text-[13px] text-neutral-400 shrink-0">Application:</div>
                            <div className="text-neutral-100 font-medium flex-1 min-w-0 truncate" title={cred.url ?? ""}>{cred.url || "—"}</div>
                            {cred.url && <CopyBtn text={cred.url} />}
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <div className="text-[13px] text-neutral-400">Username:</div>
                            <div className="text-neutral-100 truncate">{cred.username || "—"}</div>
                            {cred.username && <CopyBtn text={cred.username} />}
                          </div>
                          
                          {/* DYNAMIC FIELD FOR STEALER TYPE */}
                          {cred.password ? (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="text-[13px] text-neutral-400">Password:</div>
                              <PasswordField password={cred.password || ""} />
                              <CopyBtn text={cred.password} />
                            </div>
                          ) : cred.hashed_password ? (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="text-[13px] text-neutral-400">Hash:</div>
                              <span className="text-xs font-mono text-neutral-500 truncate max-w-[150px]">{cred.hashed_password}</span>
                              <CopyBtn text={cred.hashed_password} />
                            </div>
                          ) : null}

                          {/* Cookie availability — appears only when the
                              credential record carries cookie_available=true.
                              "See" fetches the cookie on demand; the cookie
                              value expands inline below the row. */}
                          {cred.cookie_available && (
                            <div className="mt-1">
                              <div className="flex items-center gap-2">
                                <div className="text-[13px] text-neutral-400">Cookie:</div>
                                <span className="inline-flex items-center gap-1 text-[12px] text-sky-300">
                                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-400" />
                                  Available
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleViewCookie(origIdx, cred)}
                                  disabled={cookieState?.loading}
                                  className="inline-flex items-center gap-1 rounded-md border border-neutral-700 bg-neutral-900/50 px-2 py-0.5 text-[11px] font-medium text-neutral-200 hover:bg-neutral-900 hover:border-neutral-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {cookieState?.loading ? (
                                    <>
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                      Loading
                                    </>
                                  ) : cookieState?.visible && cookieState?.data != null ? (
                                    <>
                                      <EyeOff className="h-3 w-3" />
                                      Hide
                                    </>
                                  ) : (
                                    <>
                                      <Eye className="h-3 w-3" />
                                      See
                                    </>
                                  )}
                                </button>
                              </div>
                              {cookieState?.visible && cookieState?.error && (
                                <div className="mt-1.5 rounded-md border border-rose-500/30 bg-rose-500/5 px-2 py-1.5 text-[11px] text-rose-300">
                                  {cookieState.error}
                                </div>
                              )}
                              {cookieState?.visible && cookieState?.data && (() => {
                                // Filter cookies by the per-credential search
                                // query. Matches against name, value, and
                                // domain (case-insensitive). Empty query
                                // returns the unfiltered list.
                                const cookieQuery = (cookieSearchByIdx[origIdx] || "").trim().toLowerCase();
                                const allCookies = cookieState.data.cookies;
                                const filteredCookies = cookieQuery
                                  ? allCookies.filter((ck) =>
                                      (ck.name || "").toLowerCase().includes(cookieQuery) ||
                                      (ck.value || "").toLowerCase().includes(cookieQuery) ||
                                      (ck.domain || "").toLowerCase().includes(cookieQuery)
                                    )
                                  : allCookies;
                                const isFiltering = cookieQuery.length > 0;
                                return (
                                <div className="mt-1.5 rounded-md border border-neutral-800 bg-[#000414]/60">
                                  {/* Cookie group header — small uppercase
                                      "Cookies" label, domain, count, with
                                      Copy-all action right-aligned. */}
                                  <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-neutral-800/80">
                                    <span className="text-[10px] uppercase tracking-[0.1em] font-semibold text-neutral-500">
                                      Cookies
                                    </span>
                                    <span className="font-mono text-[11px] text-neutral-300">
                                      {cookieState.data.domain || "—"}
                                    </span>
                                    <span className="text-[11px] text-neutral-600">·</span>
                                    <span className="text-[11px] text-neutral-400">
                                      {isFiltering
                                        ? `${filteredCookies.length} of ${cookieState.data.total}`
                                        : `${cookieState.data.total} found`}
                                    </span>
                                    {allCookies.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => handleCopyAllCookies(origIdx, filteredCookies)}
                                        className="ml-auto inline-flex items-center gap-1 rounded border border-neutral-700 bg-neutral-900/40 px-1.5 py-0.5 text-[10.5px] font-medium text-neutral-300 hover:bg-neutral-900 hover:border-neutral-600 transition-colors"
                                        title={isFiltering ? "Copy filtered cookies as JSON" : "Copy all cookies as JSON"}
                                      >
                                        <Copy className="h-3 w-3" />
                                        {copiedAllForIdx === origIdx ? "Copied" : isFiltering ? "Copy filtered" : "Copy all"}
                                      </button>
                                    )}
                                  </div>
                                  {/* Filter input — appears below header when
                                      there are enough cookies to warrant a
                                      search affordance (≥ 4). */}
                                  {allCookies.length >= 4 && (
                                    <div className="px-2.5 py-1.5 border-b border-neutral-800/80">
                                      <div className="relative">
                                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-neutral-500" />
                                        <input
                                          type="text"
                                          value={cookieSearchByIdx[origIdx] || ""}
                                          onChange={(e) =>
                                            setCookieSearchByIdx((prev) => ({
                                              ...prev,
                                              [origIdx]: e.target.value,
                                            }))
                                          }
                                          placeholder="Filter by name, value, or domain…"
                                          className="w-full rounded border border-neutral-800 bg-neutral-900/40 pl-7 pr-7 py-1 text-[11px] text-neutral-200 placeholder:text-neutral-500 focus:outline-none focus:border-sky-500/50 focus:bg-neutral-900/70"
                                        />
                                        {isFiltering && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setCookieSearchByIdx((prev) => ({
                                                ...prev,
                                                [origIdx]: "",
                                              }))
                                            }
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-neutral-700/40"
                                            title="Clear search"
                                          >
                                            <X className="h-3 w-3 text-neutral-500 hover:text-neutral-200" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {allCookies.length === 0 ? (
                                    <div className="px-2.5 py-2 text-[11px] text-neutral-500">No cookies returned.</div>
                                  ) : filteredCookies.length === 0 ? (
                                    <div className="px-2.5 py-2 text-[11px] text-neutral-500">
                                      No cookies match "{cookieSearchByIdx[origIdx]}".
                                    </div>
                                  ) : (
                                    <ul className="divide-y divide-neutral-800/80 max-h-72 overflow-y-auto">
                                      {filteredCookies.map((ck, ckIdx) => {
                                        const expiresMs = ck.expires ? Number(ck.expires) * 1000 : null;
                                        const expiresStr = expiresMs && !Number.isNaN(expiresMs)
                                          ? new Date(expiresMs).toISOString().replace("T", " ").slice(0, 16) + " UTC"
                                          : null;
                                        const expired = expiresMs && expiresMs < Date.now();
                                        // Build the flag/metadata text inline
                                        // instead of colored chips — RF pattern
                                        // is dot-separated muted text.
                                        const flags = [];
                                        if (ck.secure) flags.push("Secure");
                                        if (ck.http_only) flags.push("HttpOnly");
                                        if (ck.same_site) flags.push(`SameSite=${ck.same_site}`);
                                        if (ck.include_subdomains) flags.push("IncludeSubdomains");
                                        return (
                                          <li key={`${ck.source_id}-${ck.name}-${ckIdx}`} className="px-2.5 py-2">
                                            {/* Header row: name + browser */}
                                            <div className="flex items-baseline gap-2">
                                              <span className="font-mono text-[12px] text-neutral-100 font-semibold break-all">
                                                {ck.name || "—"}
                                              </span>
                                              {ck.source_browser && (
                                                <span className="ml-auto text-[10px] text-neutral-500">
                                                  {ck.source_browser}
                                                </span>
                                              )}
                                            </div>
                                            {/* Domain · path subtitle */}
                                            {(ck.domain || ck.path) && (
                                              <div className="font-mono text-[10.5px] text-neutral-500 mt-0.5 break-all">
                                                {ck.domain || ""}{ck.path ? ` · ${ck.path}` : ""}
                                              </div>
                                            )}
                                            {/* Value */}
                                            <div className="mt-1.5 flex items-start gap-2 rounded bg-neutral-900/40 px-2 py-1">
                                              <pre className="flex-1 min-w-0 text-[11px] font-mono text-neutral-300 whitespace-pre-wrap break-all leading-snug max-h-20 overflow-y-auto">
{ck.value || "(empty)"}
                                              </pre>
                                              {ck.value && <CopyBtn text={ck.value} />}
                                            </div>
                                            {/* Meta row — inline dot-separated
                                                text, color only on Expired. */}
                                            <div className="mt-1.5 text-[10.5px] text-neutral-500 flex items-center gap-1.5 flex-wrap">
                                              {expiresStr && (
                                                <span className={expired ? "text-rose-300" : ""}>
                                                  {expired ? "Expired " : "Expires "}{expiresStr}
                                                </span>
                                              )}
                                              {flags.length > 0 && (
                                                <>
                                                  {expiresStr && <span>·</span>}
                                                  <span>{flags.join(" · ")}</span>
                                                </>
                                              )}
                                              {ck.source_id != null && (
                                                <span className="ml-auto font-mono">src #{ck.source_id}</span>
                                              )}
                                            </div>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                        </React.Fragment>
                        );
                        });
                      })()
                    ) : (
                      <div className="text-[13px] text-neutral-400">No credentials available.</div>
                    )}
                  </div>
                </Section>

                <Section title="Remediations">
                  <p className="text-[13px] text-neutral-300 leading-relaxed">Disconnect infected device, run malware scan, reset passwords, enable MFA, and report to IT security.</p>
                </Section>
              </>
            )}
          </div>
        )}

        {/* TAB VISIBILITY LOGIC */}
{tab === "History" && initialParams?.type !== "database" && (
  <div className="space-y-6">
    {data?.data?.systeminfo?.length ? (
      <>
        <ul className="relative ml-2 pl-6 border-l border-neutral-800 space-y-6">
          {data.data.systeminfo.map((info, idx) => {
            return (
              <li key={info.systeminfo_id ?? idx} className="relative">

                <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-sky-500 shadow ring-4 ring-[#000414]" />
                <div className="rounded-md bg-neutral-900/40 border border-neutral-800 p-4">
                  <div className="mb-3 flex items-center gap-2 gap-y-2 flex-wrap text-xs font-semibold text-sky-400">
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    <span>{info.log_date || "Unknown Date"}</span>
                    {idx === 0 && (
                      <span className="ml-1 rounded-full bg-sky-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-300">
                        Latest
                      </span>
                    )}
                  </div>

                  <Grid>
                    {Object.entries(info).map(([key, value]) => {
                      // Skip metadata fields we display separately
if (["log_date", "systeminfo_id", "archive_status", "archive_size_bytes"].includes(key)) return null;

                      const lowerKey = key.toLowerCase();
                      let icon = <Hash className="h-3.5 w-3.5" />;
                      if (lowerKey.includes("ip")) icon = <Network className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("country")) icon = <Globe className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("host") || lowerKey.includes("computer")) icon = <Terminal className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("os") || lowerKey.includes("windows")) icon = <Cpu className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("virus") || lowerKey.includes("defender")) icon = <ShieldCheck className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("path") || lowerKey.includes("file")) icon = <FileWarning className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("id") || lowerKey.includes("guid")) icon = <Fingerprint className="h-3.5 w-3.5" />;
                      return <EnterpriseField key={key} icon={icon} label={key.replace(/_/g, " ")} value={value} />;
                    })}
                  </Grid>
                </div>
              </li>
            );
          })}
        </ul>
      </>
    ) : (
      <div className="text-[13px] text-neutral-400">No historical records available.</div>
    )}
  </div>
)}

{/* ─────────────────── DOWNLOADS TAB ─────────────────── */}
{/* Focused view of all archives that are currently downloadable for this
    incident. Same trigger flow (warning popover → confirm) as History. */}
{tab === "Downloads" && initialParams?.type !== "database" && (
  <div className="space-y-4">
    {downloadableInfos.length === 0 ? (
      <div className="text-[13px] text-neutral-400">No downloadable archives available.</div>
    ) : (
      <>
        {/* Summary banner */}
        <div className="rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2.5 text-[12.5px] text-sky-200 flex items-center gap-2">
          <Download className="h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold">{downloadableInfos.length}</span>{" "}
            downloadable archive{downloadableInfos.length === 1 ? "" : "s"} available
            for this incident.
          </span>
        </div>

        {/* Archive cards */}
        <ul className="space-y-3">
          {downloadableInfos.map((info, idx) => {
            const archive = getArchiveState(info);
            const isDownloading = archiveDownloadingId === info.systeminfo_id;
            const sizeText = formatBytes(info.archive_size_bytes);
            return (
              <li key={info.systeminfo_id ?? idx} className="rounded-md border border-sky-900/30 bg-neutral-900/40 p-4">
                {/* Header row: date + status + size + download button */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[13px] font-semibold text-sky-400">
                      <Calendar className="h-3.5 w-3.5 shrink-0" />
                      <span>{info.log_date || "Unknown Date"}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <ArchiveBadge state={archive} />
                      {sizeText !== "—" && (
                        <span className="inline-flex items-center gap-1 rounded-md border border-sky-900/40 bg-[#0a1224]/60 px-1.5 py-0.5 text-[10.5px] font-mono tabular-nums text-neutral-300">
                          <HardDrive className="h-3 w-3 text-neutral-500" />
                          {sizeText}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Download button + warning popover — same flow as History */}
                  <div className="relative shrink-0" data-archive-warning>
                    <button
                      onClick={() => setPendingDownloadId(info.systeminfo_id)}
                      disabled={isDownloading || archiveDownloadingId !== null}
                      title="Download this snapshot's archive"
                      className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/50 bg-sky-500/15 px-3 py-1.5 text-[12.5px] font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                    >
                      {isDownloading ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Preparing…
                        </>
                      ) : (
                        <>
                          <Download className="h-3.5 w-3.5" />
                          Download Full Log
                        </>
                      )}
                    </button>
                    {pendingDownloadId === info.systeminfo_id && (
                      <div
                        data-archive-warning
                        className="absolute right-0 top-full mt-2 z-50 w-72 rounded-md border border-amber-500/40 bg-[#000414] shadow-2xl text-left"
                      >
                        <div className="px-4 pt-3.5">
                          <div className="flex items-center gap-2 mb-1.5">
                            <ShieldAlert className="h-4 w-4 text-amber-300 shrink-0" />
                            <h3 className="text-[13px] font-semibold text-white">Heads up</h3>
                          </div>
                          <p className="text-xs text-neutral-300 leading-relaxed">
                            Infostealer archives may contain malware. Investigate
                            in a sandboxed environment — preferably a Linux VM.
                          </p>
                        </div>
                        <div className="px-3 py-2.5 mt-2 border-t border-white/5 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setPendingDownloadId(null)}
                            className="rounded-md border border-neutral-700 bg-neutral-900/40 px-2.5 py-1 text-xs text-neutral-200 hover:bg-neutral-900 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const id = pendingDownloadId;
                              setPendingDownloadId(null);
                              handleDownloadArchive(id);
                            }}
                            className="rounded-md border border-amber-500/40 bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/25 transition-colors"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick-context strip — hostname / IP / country */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-sky-900/30 text-[12px]">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Host</div>
                    <div className="text-neutral-200 font-mono truncate" title={info.hostname || ""}>
                      {info.hostname || "—"}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">IP</div>
                    <div className="text-neutral-200 font-mono truncate" title={info.ip || ""}>
                      {info.ip || "—"}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Country</div>
                    <div className="text-neutral-200 truncate" title={info.country || ""}>
                      {info.country || "—"}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </>
    )}
  </div>
)}
      </div>

{isLocked && (
  <div className="absolute inset-x-0 top-[18%] z-20 flex justify-center px-6">
    <div className="w-full max-w-md rounded-md border border-sky-900/40 bg-[#0a1224]/95 p-5">
      {/* Header — small icon + uppercase eyebrow, matching the rest of the app's panel headers. */}
      <div className="flex items-center gap-2 mb-3">
        <Shield className="h-3.5 w-3.5 text-sky-300/80" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold">
          {initialParams?.type === "database" ? "Subscription required" : "Record locked"}
        </span>
      </div>

      {initialParams?.type === "database" ? (
        <>
          <h3 className="text-[15px] font-semibold text-white tracking-tight">
            Database breaches require an active subscription
          </h3>
          <p className="mt-1.5 text-[13px] text-neutral-400 leading-relaxed">
            Individual unlocks aren't available for this data type. Upgrade your plan to access full breach forensics.
          </p>

          {/* Hairline-separated preview note — what's behind the lock. */}
          <div className="mt-4 pt-3 border-t border-sky-900/30">
            {(data?.data?.accounts?.[0]?.password || data?.data?.accounts?.[0]?.hashed_password) ? (
              <div className="flex items-start gap-2.5">
                <Key className="h-3.5 w-3.5 mt-0.5 text-emerald-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-300/90">Credentials available</div>
                  <p className="mt-0.5 text-[12px] text-neutral-400 leading-relaxed">
                    Upgrading reveals the compromised password or hash tied to this identity.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5">
                <Info className="h-3.5 w-3.5 mt-0.5 text-neutral-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400">Identity only</div>
                  <p className="mt-0.5 text-[12px] text-neutral-500 leading-relaxed">
                    This specific record contains identity data and PII. No passwords or hashes were recovered in this breach.
                  </p>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate('/upgrades')}
            className="mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-2 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 transition-colors"
          >
            Upgrade license
          </button>
        </>
      ) : (
        <>
          <h3 className="text-[15px] font-semibold text-white tracking-tight">
            Unlock to view full record
          </h3>
          <p className="mt-1.5 text-[13px] text-neutral-400 leading-relaxed">
            Spend one unlock credit to reveal credentials, host metadata, and capture history for this incident.
          </p>

          <button
            onClick={handleUnlock}
            disabled={unlocking}
            className="mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed px-3.5 py-2 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 transition-colors"
          >
            {unlocking ? "Unlocking…" : "Unlock entry"}
          </button>
        </>
      )}
    </div>
  </div>
)}
    </>
  )}
</div>
      </aside>
      {cfg && (
        <FrostedModal
          open={modal.open}
          onClose={closeModal}
          icon={cfg.icon}
          eyebrow={cfg.eyebrow}
          title={modal.type === MODAL_TYPE.SECURITY_RESTRICTION && restriction?.title ? restriction.title : cfg.title}
          description={modal.type === MODAL_TYPE.SECURITY_RESTRICTION && restriction?.message ? restriction.message : cfg.description}
          primaryAction={cfg.primary && { label: cfg.primary.label, onClick: () => { if (cfg.primary.href) window.location.href = cfg.primary.href; else closeModal(); } }}
          secondaryAction={cfg.secondary && { label: cfg.secondary.label, onClick: () => { if (cfg.secondary.href) window.location.href = cfg.secondary.href; else closeModal(); } }}
        />
      )}

    </div>
  );
}

function IconBtn({ children, title, onClick, disabled }) {
  return (<button className={`p-2 rounded ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5"}`} title={title} onClick={onClick} disabled={disabled}>{children}</button>);
}

const fmtApiError = (res, fallback) => [fallback, res?.message || res?.error || res?.errors?.[0]?.message].filter(Boolean).join(" — ");

function Section({ title, children }) {
  return (<section><h3 className="mb-3 text-[13px] font-semibold tracking-wide text-neutral-400 uppercase">{title}</h3>{children}</section>);
}

function Grid({ children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-[13px]">
      {children}
    </div>
  );
}

// src/components/DetailSidebar.jsx (Bottom of file)

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      {/* Changed text-neutral-400 to text-neutral-500/80 for better contrast on gray bg */}
      <div className="text-xs font-medium uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 truncate text-[13px] text-neutral-200">
        {value || "—"}
      </div>
    </div>
  );
}

function CopyBtn({ text }) {
  return (<button onClick={() => navigator.clipboard.writeText(text)} className="p-1 rounded hover:bg-white/5" title="Copy to clipboard"><Copy className="h-4 w-4 text-neutral-400 hover:text-white" /></button>);
}

function PasswordField({ password }) {
  const [visible, setVisible] = useState(false);
  return (
    <>
      <span className="text-neutral-100">{visible ? (password || "—") : "••••••••"}</span>
      <button onClick={() => setVisible((v) => !v)} className="p-1 rounded hover:bg-white/5" title={visible ? "Hide password" : "Show password"}>
        {visible ? <EyeOff className="h-4 w-4 text-neutral-400 hover:text-white" /> : <Eye className="h-4 w-4 text-neutral-400 hover:text-white" />}
      </button>
    </>
  );
}
function EnterpriseField({ label, value, icon, highlight = false }) {
  // Logic to handle empty values gracefully
  const displayValue = value && String(value).trim() !== "" && value !== "N/A" 
    ? value 
    : "—";

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
        {icon && <span className="opacity-70">{icon}</span>}
        <span>{label}</span>
      </div>
      <div 
        className={`truncate font-mono text-[13px] ${
          highlight ? "text-white" : "text-neutral-300"
        }`} 
        title={displayValue}
      >
        {displayValue}
      </div>
    </div>
  );
}


function ArchiveBadge({ state }) {
  const styleByTone = {
    available: "border-sky-500/30 bg-sky-500/10 text-sky-300",
    dedup:     "border-sky-500/30 bg-sky-500/10 text-sky-300",
    pending:   "border-amber-500/30 bg-amber-500/10 text-amber-300",
    failed:    "border-red-500/30 bg-red-500/10 text-red-300",
    none:      "border-neutral-700 bg-neutral-800/40 text-neutral-500",
  };

  const tooltip = {
    available: "Full log archive is available for download",
    dedup:     "Archive deduplicated — identical content to a previous snapshot",
    pending:   "Archive is currently being uploaded",
    failed:    "Archive upload failed — retry in progress",
    none:      "No archive was stored for this incident",
  }[state.tone];

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${styleByTone[state.tone]}`}
      title={tooltip}
    >
      <HardDrive className="h-3 w-3" />
      <span>{state.label}</span>
    </span>
  );
}